import type { ReactNode } from 'react';
import { HeadContent, Scripts, createRootRouteWithContext } from '@tanstack/react-router';
import { Providers } from '../app/Providers';
import type { AppRouterContext } from '../query/client';
import { serializeJsonLd } from '../lib/jsonLd';
import { WebMcp } from '../app/WebMcp';
import { SiteLayout } from '../layout/SiteLayout';
import { NotFoundPage } from '../pages/NotFoundPage';
import { SITE_SCHEMA_GRAPH } from '../lib/seo/orgSchema';
import { CATALOG_API_COUNT } from '../lib/catalogSize';
import { getLocale } from '../paraglide/runtime';
import { ogLocale } from '../i18n/localeHead';
import { m } from '../paraglide/messages';
// A side-effect import, deliberately not `?url`. With `?url` the sheet is a Vite
// asset outside the route manifest, so it can only ever be a <link> and the
// browser cannot request it until it has parsed <head> - a second round trip
// that gated first paint. Imported this way it is route CSS, which
// `server.build.inlineCss` (vite.config.ts) inlines into the prerendered
// document. Measured on a production build served with brotli, Slow 4G + 4x
// CPU: FCP 1722ms -> 945ms, LCP 1722ms -> 1436ms, spreads non-overlapping.
// Nothing else came close - lazy-loading the below-fold image preloads, 103
// Early Hints for the sheet, and halving the document all measured zero.
import '../styles/app.css';

// Title and description carry different jobs and must not restate each other.
// The title names what you get and why; the description spends every clause on
// something the title did not say: scale, mechanism, reliability, payment, entry.
//
// Sentence case, and the casing is load-bearing rather than a preference: this
// is the back half of the `h1` (HeroFrame.tsx, "We route you to the cheapest,
// most reliable data"), so a capitalised `Cheapest` or `Data` here puts two
// spellings of one sentence on one page. Title-casing the nouns is the edit to
// resist, and the claim itself has to keep tracking that `h1`.
//
// Both are functions of the locale, read when the head renders, so a page
// under `/zh` gets the Chinese title without a route of its own.
const siteTitle = () => m.site_title();
const siteDescription = () => m.site_description({ count: CATALOG_API_COUNT });

// Pre-paint theme script: applies the persisted theme if the user explicitly
// chose one, otherwise falls back to the OS `prefers-color-scheme`. Runs before
// first paint to avoid a flash.
//
// It also marks the document as scripted. Any rule that holds content back for
// JS to release - the landing page's scroll-gated section motion is the one
// today - hangs off `.js`, so a document that never runs its bundle renders
// those sections at rest instead of at frame zero, which is blank.
const THEME_INIT = `(function(){try{document.documentElement.classList.add('js');var s=localStorage.getItem('anyapi-theme-a');var t=s==='dark'||s==='light'?s:(window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light');document.documentElement.setAttribute('data-theme',t);}catch(e){document.documentElement.setAttribute('data-theme','light');}})();`;

// The SSR document shell. `head()` produces the static <head> that crawlers see
// without running JS - this is the SEO win. Per-route head() merges over this
// (titles, canonical, og:image, JSON-LD).
export const Route = createRootRouteWithContext<AppRouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { title: siteTitle() },
      { name: 'description', content: siteDescription() },
      { name: 'theme-color', content: '#1f4fe0' },
      // Domain-ownership proof for the backlinkexchange.org listing. It reads
      // the homepage head, so this sits at the root rather than on one route.
      { name: 'backlink-exchange-verification', content: '9b0d04431f94ffbb698925c1beec9850' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'AnyAPI' },
      { property: 'og:title', content: siteTitle() },
      { property: 'og:description', content: siteDescription() },
      { property: 'og:url', content: 'https://getanyapi.com/' },
      { property: 'og:locale', content: ogLocale() },
      { property: 'og:image', content: 'https://getanyapi.com/og-v10.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@getanyapi' },
      { name: 'twitter:creator', content: '@mxfp4' },
      { name: 'twitter:image', content: 'https://getanyapi.com/og-v10.png' },
      { name: 'twitter:title', content: siteTitle() },
      { name: 'twitter:description', content: siteDescription() },
    ],
    links: [
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico', sizes: '16x16 32x32 48x48' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      // No canonical here: TanStack merges `links` by concatenation (no dedupe
      // by rel), so a root-level canonical would stack a second <link
      // rel="canonical"> on every page that sets its own. Each route declares
      // its own self-referencing canonical instead (index.tsx covers "/").
      // Self-hosted fonts (declared in tokens.css). Preload the two faces that
      // render above-the-fold text so they're fetched before CSS parses. Geist
      // (variable woff2) covers the hero headline. JetBrains Mono renders the
      // hero terminal prompt - the LCP element; it's `font-display: optional`
      // (tokens.css) so a late load never re-times LCP (that swap cost ~+2.2s on
      // throttled mobile), and this preload gives fast connections a chance to
      // still paint it within the optional block window.
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/geist.woff2',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: '/fonts/jetbrains-mono.woff2',
        crossOrigin: 'anonymous',
      },
      // No Clerk preconnect. It earned its ~310ms LCP win back when Clerk loaded
      // on every marketing page; now that auth is scoped to `/sign-in`,
      // `/sign-up`, and `/dashboard` (see `auth/ClerkScope`), a sitewide
      // preconnect would open a TLS connection the landing page never uses.
    ],
  }),
  shellComponent: RootDocument,
  notFoundComponent: () => (
    <SiteLayout>
      <NotFoundPage />
    </SiteLayout>
  ),
});

function RootDocument({ children }: { children: ReactNode }) {
  return (
    // The pre-paint THEME_INIT script sets `data-theme` on <html> before React
    // hydrates, so the live attribute differs from the server markup. Suppress
    // the (expected) hydration attribute warning on this one element.
    <html lang={getLocale()} suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT }} />
        {/* Sitewide Organization + WebSite entity graph, referenced by @id from
            per-page schema (BlogPosting publisher, author worksFor). */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(SITE_SCHEMA_GRAPH) }}
        />
        <HeadContent />
      </head>
      <body>
        <Providers>{children}</Providers>
        <WebMcp />
        <Scripts />
      </body>
    </html>
  );
}
